import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
// Fase 5.17 -- `useCurrentUser` es un selector zustand a nivel de módulo, NO React context
// (misma excepción documentada que 5.10/5.16). `toast` sí llega por prop.
// eslint-disable-next-line no-restricted-imports
import { useCurrentUser } from '../../../../context/authHooks';
import { useDragResize } from '../../../../shared/hooks/useDragResize';
import { useWorkframeIndexTree } from '../../hooks/useWorkframeIndexTree';
import { useWorkframeIndexDnD } from '../../hooks/useWorkframeIndexDnD';
import { useWorkframePref } from '../../hooks/useWorkframePrefs';
import { useDeleteDoc } from '../../../docs/hooks/useDocsData';
import WorkframeIndexNode from './WorkframeIndexNode';
import WorkframeIndexToolbar from './WorkframeIndexToolbar';

// Columna 1 del Cuadro de Trabajo: Índice (integración main→dev, epic D1.3). Contenedor:
// compone `useWorkframeIndexTree` (datos/estado) + `useWorkframeIndexDnD` (reorden) + el resize
// del borde, y delega el render recursivo a `WorkframeIndexNode`. El `main` original tenía las
// 810 líneas en un solo archivo.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: hidrata el ancho guardado de la columna una vez cargada la preferencia.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const MIN_WIDTH = 180;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 260;

function clampWidth(raw) {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return null;
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parsed));
}

export default function WorkframeIndexColumn({
    spaceId,
    rootDocId,
    selectedDocId,
    onSelectDoc,
    onDocCreated,
    onWidthChange,
    toast,
}) {
    const { t } = useTranslation('tasks');
    const { dbId } = useCurrentUser();

    const {
        rawDocs, tree, groupedTree, searchState, searchTerm, setSearchTerm,
        openIds, toggleOpen, isAllExpanded, toggleExpandAll,
        creatingParentId, setCreatingParentId, createSubdoc, isLoading,
    } = useWorkframeIndexTree({ spaceId, rootDocId, selectedDocId });

    const dnd = useWorkframeIndexDnD({
        rawDocs,
        rootDocId,
        onReordered: () => toast.success(t('workframe.index.positionUpdated')),
        onError: () => toast.error(t('workframe.index.positionUpdateFailed')),
    });

    const [newDocTitle, setNewDocTitle] = useState('');
    const [isEditingTagsMode, setIsEditingTagsMode] = useState(false);
    const selectedItemRef = useRef(null);

    // Ancho del índice — espejado en `/api/customs` vía React Query (no `localStorage`).
    const widthPref = useWorkframePref(dbId, 'workframe_index_width', { enabled: !!dbId, coerce: clampWidth });
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    const widthHydratedRef = useRef(false);
    const resizeStartWidthRef = useRef(DEFAULT_WIDTH);

    useEffect(() => {
        if (widthHydratedRef.current) return;
        if (widthPref.isLoaded) {
            widthHydratedRef.current = true;
            if (widthPref.value) {
                setWidth(widthPref.value);
                onWidthChange?.(widthPref.value);
            }
        }
    }, [widthPref.isLoaded, widthPref.value, onWidthChange]);

    const applyWidth = useCallback((next) => {
        setWidth(next);
        onWidthChange?.(next);
    }, [onWidthChange]);

    const startResize = useDragResize({
        onMove: ({ startX, initialValue, moveEvent }) => {
            setIsResizing(true);
            const dx = moveEvent.clientX - startX;
            let next = (initialValue?.width ?? DEFAULT_WIDTH) + dx;
            next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next));
            applyWidth(next);
            return next;
        },
        onEnd: (finalVal) => {
            setIsResizing(false);
            const finalWidth = typeof finalVal === 'number' ? finalVal : finalVal?.width;
            if (finalWidth && finalWidth !== resizeStartWidthRef.current) {
                widthPref.save(String(finalWidth));
                toast.success(t('workframe.index.widthSaved'));
            }
        },
    });

    const handleResizePointerDown = (e) => {
        e.stopPropagation();
        resizeStartWidthRef.current = width;
        startResize(e, { width });
    };

    useEffect(() => {
        if (selectedDocId && selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedDocId]);

    // Ref to track if we've processed the initial URL hash yet
    const isInitialHashSyncRef = useRef(true);

    // Deep linking: Sync selectedDocId TO window.location.hash
    useEffect(() => {
        if (!tree || tree.length === 0 || !selectedDocId) return;

        // Prevent overwriting the initial user-provided hash before we've had a chance to read it
        if (isInitialHashSyncRef.current && window.location.hash && window.location.hash.length > 1) {
            return;
        }

        const findNodeById = (nodes, id) => {
            for (const node of nodes) {
                if (node.id === id) return node;
                if (node.children) {
                    const found = findNodeById(node.children, id);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedNode = findNodeById(tree, selectedDocId);
        if (selectedNode && selectedNode.nodeNumber) {
            const expectedHash = `#${selectedNode.nodeNumber}`;
            if (window.location.hash !== expectedHash) {
                window.history.replaceState(null, '', expectedHash);
            }
        }
    }, [tree, selectedDocId]);

    // Deep linking: Sync window.location.hash TO selectedDocId
    useEffect(() => {
        if (!tree || tree.length === 0) return;

        const findNodeByNumber = (nodes, num) => {
            for (const node of nodes) {
                if (node.nodeNumber === num) return node;
                if (node.children) {
                    const found = findNodeByNumber(node.children, num);
                    if (found) return found;
                }
            }
            return null;
        };

        const syncFromHash = () => {
            const hash = window.location.hash;
            if (hash && hash.length > 1) {
                const targetNumber = decodeURIComponent(hash.substring(1));
                const foundNode = findNodeByNumber(tree, targetNumber);
                if (foundNode) {
                    onSelectDoc(foundNode.id);
                }
            }
        };

        if (isInitialHashSyncRef.current && window.location.hash) {
            isInitialHashSyncRef.current = false;
            syncFromHash();
        }

        // eslint-disable-next-line no-restricted-syntax
        window.addEventListener('hashchange', syncFromHash);
        // eslint-disable-next-line no-restricted-syntax
        return () => window.removeEventListener('hashchange', syncFromHash);
    }, [tree, onSelectDoc]);

    const handleStartCreate = useCallback((parentId) => {
        setCreatingParentId(parentId);
        setNewDocTitle('');
    }, [setCreatingParentId]);

    const handleSubmitCreate = useCallback(async (parentId) => {
        const created = await createSubdoc(parentId, newDocTitle || t('workframe.index.newDocDefault'));
        setNewDocTitle('');
        if (created?.id) {
            onSelectDoc(created.id);
            onDocCreated?.(created);
        }
    }, [createSubdoc, newDocTitle, t, onSelectDoc, onDocCreated]);

    const deleteDoc = useDeleteDoc();
    const handleDeleteDoc = useCallback((id, name) => {
        toast.prompt(
            `${t('workframe.index.confirmDelete', '¿Seguro que deseas borrar')} "${name}"?`,
            {
                confirmText: t('workframe.index.delete', 'Borrar'),
                cancelText: t('workframe.index.cancel', 'Cancelar'),
                onConfirm: () => {
                    deleteDoc.mutateAsync(id)
                        .then(() => toast.success(t('workframe.index.deleted', 'Documento eliminado')))
                        .catch(() => toast.error(t('workframe.index.deleteFailed', 'Error al eliminar')));
                }
            }
        );
    }, [deleteDoc, toast, t]);

    const nodeProps = {
        rootDocId,
        selectedDocId,
        selectedItemRef,
        searchTerm,
        searchState,
        openIds,
        dnd,
        creatingParentId,
        newDocTitle,
        onSelectDoc,
        onToggleOpen: toggleOpen,
        onStartCreate: handleStartCreate,
        onNewDocTitleChange: setNewDocTitle,
        onSubmitCreate: handleSubmitCreate,
        onCancelCreate: () => setCreatingParentId(null),
        onDeleteDoc: handleDeleteDoc,
        isEditingTagsMode,
        t,
    };

    const searchEmpty = searchState.isSearching && searchState.visibleIds.size === 0;

    return (
        <div
            className={`wf-column wf-column-index ${isResizing ? 'is-resizing' : ''}`}
            style={{
                '--wf-index-width': `${width}px`,
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: `${width}px`,
                flex: `0 0 ${width}px`,
            }}
        >
            <WorkframeIndexToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isAllExpanded={isAllExpanded}
                onToggleExpandAll={toggleExpandAll}
                isEditingTagsMode={isEditingTagsMode}
                onToggleEditingTagsMode={() => setIsEditingTagsMode(!isEditingTagsMode)}
                t={t}
            />

            <div className="wf-tree-scroll">
                {isLoading && tree.length === 0 ? (
                    <div className="wf-empty-state">{t('workframe.index.loading')}</div>
                ) : tree.length === 0 ? (
                    <div className="wf-empty-state">
                        <span>{t('workframe.index.empty')}</span>
                        {rootDocId && (
                            <button className="wf-create-first-btn" onClick={() => handleSubmitCreate(rootDocId)}>
                                <Plus size={13} /> {t('workframe.index.createFirst')}
                            </button>
                        )}
                    </div>
                ) : searchEmpty ? (
                    <div className="wf-empty-state">
                        <span>{t('workframe.index.noMatches', { term: searchTerm })}</span>
                    </div>
                ) : groupedTree ? (
                    groupedTree.map((group) => {
                        if (searchState.isSearching && !group.nodes.some((n) => searchState.visibleIds.has(n.id) || searchState.expandedIds.has(n.id))) {
                            return null;
                        }
                        return (
                            <div key={group.space_id} className="wf-space-group">
                                <div className="wf-space-divider">
                                    {group.space_icon
                                        ? <img src={group.space_icon} alt="" className="wf-space-divider-icon" />
                                        : <div className="wf-space-divider-icon wf-space-divider-icon-empty" />}
                                    <span className="wf-space-divider-name">
                                        {group.space_name || t('workframe.index.noSpace')}
                                    </span>
                                    <div className="wf-space-divider-line" />
                                </div>
                                {group.nodes.map((node) => (
                                    <WorkframeIndexNode key={node.id} node={node} depth={0} {...nodeProps} />
                                ))}
                            </div>
                        );
                    })
                ) : (
                    tree.map((node) => <WorkframeIndexNode key={node.id} node={node} depth={0} {...nodeProps} />)
                )}
            </div>

            <div
                className={`wf-index-resizer ${isResizing ? 'is-resizing' : ''}`}
                onPointerDown={handleResizePointerDown}
                title={t('workframe.index.resizeHandle')}
            />
        </div>
    );
}
